import React from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Calculator,
  Landmark,
  Briefcase,
  BookOpen,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    n: "01",
    title: "GST Registration & Filing",
    desc: "GSTIN registration, monthly/quarterly return filing, GSTR reconciliation and end-to-end GST advisory for businesses of every size.",
    icon: FileText,
    tags: ["GSTR-1", "GSTR-3B", "Annual Return"],
  },
  {
    n: "02",
    title: "ITR Filing",
    desc: "Accurate Income Tax Return filing for individuals, professionals, partnerships and companies — with maximum legitimate savings.",
    icon: Calculator,
    tags: ["ITR-1", "ITR-3", "ITR-6"],
  },
  {
    n: "03",
    title: "Tax Compliance & Advisory",
    desc: "TDS filings, advance tax, notices response, and proactive advisory to keep your business fully compliant with Indian tax laws.",
    icon: Landmark,
    tags: ["TDS", "Advance Tax", "Notices"],
  },
  {
    n: "04",
    title: "Company Registration / ROC",
    desc: "Private Ltd, LLP, OPC & partnership incorporation, MCA filings, annual ROC returns and secretarial compliance handled end-to-end.",
    icon: Briefcase,
    tags: ["Pvt Ltd", "LLP", "ROC Filing"],
  },
  {
    n: "05",
    title: "Accounting & Bookkeeping",
    desc: "Clean books, MIS reports, payroll and financial statements — Tally / Zoho Books / cloud accounting configured for your workflow.",
    icon: BookOpen,
    tags: ["Tally", "Payroll", "MIS"],
  },
];

const cardAnim = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export const Services = () => {
  return (
    <section
      id="services"
      className="relative border-b border-border bg-background py-24 md:py-32"
      data-testid="services-section"
    >
      <div className="container mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="mb-4 flex items-center gap-3">
              <span className="h-px w-10 bg-primary" />
              <span className="font-[Outfit] text-xs font-bold uppercase tracking-[0.2em] text-primary">
                Services
              </span>
            </div>
            <h2 className="text-4xl font-extrabold leading-tight tracking-tight text-foreground md:text-5xl">
              Everything your business needs.<br />
              <span className="italic text-primary">Handled with care.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 md:pt-6">
            <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
              Five essential offerings, one accountable partner. From your first
              GST return to a company-wide audit, NP Associates provides a
              transparent, deadline-driven service — at fees that make sense.
            </p>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 border-t border-l border-border sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.n}
                {...cardAnim}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group relative border-b border-r border-border bg-background p-8 transition hover:bg-secondary/40"
                data-testid={`service-card-${s.n}`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-[Outfit] text-xs font-bold tracking-widest text-muted-foreground">
                    / {s.n}
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-muted-foreground opacity-0 transition group-hover:opacity-100" />
                </div>

                <div className="mt-8 flex h-12 w-12 items-center justify-center border border-border bg-white transition group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground">
                  <Icon className="h-5 w-5" />
                </div>

                <h3 className="mt-6 font-[Outfit] text-2xl font-bold leading-tight tracking-tight text-foreground">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {s.desc}
                </p>

                <div className="mt-6 flex flex-wrap gap-2">
                  {s.tags.map((t) => (
                    <span
                      key={t}
                      className="border border-border px-2 py-0.5 font-[Outfit] text-[10px] font-semibold uppercase tracking-widest text-foreground/70"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </motion.article>
            );
          })}

          <motion.a
            {...cardAnim}
            transition={{ duration: 0.5, delay: services.length * 0.06 }}
            href="#contact"
            className="group flex flex-col justify-between border-b border-r border-border bg-foreground p-8 text-background transition hover:bg-primary"
            data-testid="services-cta-card"
          >
            <div>
              <span className="font-[Outfit] text-xs font-bold tracking-widest text-background/60">
                / GET STARTED
              </span>
              <h3 className="mt-6 font-[Outfit] text-2xl font-bold leading-tight tracking-tight">
                Not sure what you need? We&apos;ll guide you.
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-background/70">
                Free 15-minute consultation to understand your requirement and
                give you a fixed, affordable quote.
              </p>
            </div>
            <div className="mt-8 flex items-center gap-2 font-[Outfit] text-sm font-semibold uppercase tracking-wider">
              Book a call
              <ArrowUpRight className="h-4 w-4 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </div>
          </motion.a>
        </div>
      </div>
    </section>
  );
};

export default Services;
